import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { trigger, style, animate, transition, AnimationEvent, sequence } from '@angular/animations';

import { Subscription } from 'rxjs';
import { DomHandler } from 'primeng/dom';
import * as uuid from 'uuid';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { InteractionViewService } from '../../services/interaction-view.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { TargetingRhpContextsService } from '../../../TargetingShared/services/targeting-rhp-contexts.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';


@Component({
    selector: 'agpr-rhp-contexts-takeover',
    templateUrl: './rhp-contexts-takeover.component.html',
    styleUrls: ['./rhp-contexts-takeover.component.scss'],
    providers: [TargetingRhpContextsService, InteractionViewService],
    animations: [
        trigger('overlayAnimation', [
            transition('* => open', [
                style({ opacity: 0 }),
                sequence([
                    animate('0.15s', style({ opacity: 1 })),
                ])
            ]),
            transition('* => close', [
                style({ opacity: 1 }),
                sequence([
                    animate('0.15s', style({ opacity: 0 })),
                ])
            ])
        ])
    ],
    standalone: false
})
export class RhpContextsTakeoverComponent extends AbstractDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() contactId: string;
    @Input() outletId: string;
    @Input() entityType: EntityType;
    @Input() isPrivate: boolean;
    @Input() fullHeight: boolean = false;
    @Input() disableContentToggle: boolean = false;
    // TODO : Initial selected employment probably needed for contacts

    private subscriptions: Subscription[] = [];
    zIndex: number;

    readonly overlayAnimationStateEnum = OverlayAnimationStateEnum;
    readonly buttonTypes = ButtonTypesEnum;

    overlayAnimation: OverlayAnimationStateEnum;
    enableClickOutside: boolean = false;
    clickOutsideExcluded: string = '.agpr-clickOutsideExcluded';
    dialogLabel: string = '';

    constructor(
        public coreDialogService: CoreDialogService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public localizationService: LocalizationService
    ) {
        super(coreDialogService, uuid.v4());
        this.zIndex = ++DomHandler.zindex;
    }

    ngOnInit() {
        this.subscriptions.push(
            this.targetingRhpContextsService.getCloseTakeOverProvider().subscribe(() => {
                this.closeDialog();
            })
        );
        this.overlayAnimation = OverlayAnimationStateEnum.open;
        this.dialogLabel = this.localizationService.transform(this.entityType === EntityType.CONTACT ?
            'Discover.targeting.rhp.contactDetails' : 'Discover.targeting.rhp.outletDetails');
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    close(source?: any) {
        // Exclude doesn't seem to work, so this is a manual exclude, specifically for the employment dropdown
        if (source?.path) {
            for (const pathElement of source.path) {
                if (pathElement.className && pathElement.className.indexOf('agpr-clickOutsideExcluded') !== -1) {
                    return;
                }
            }
        }
        this.overlayAnimation = OverlayAnimationStateEnum.close;
        this.closeDialog(this.key);
    }

    animationDone(event: AnimationEvent) {
        if (event.toState === OverlayAnimationStateEnum.open) {
            this.enableClickOutside = true;
        } else if (event.toState === OverlayAnimationStateEnum.close) {
            this.closeDialog(this.key);
        }
    }

    ngAfterViewInit() {
        if (this.entityType === EntityType.CONTACT) {
            this.targetingRhpContextsService.setContact(this.contactId);
            if (!this.isPrivate) {
                this.targetingRhpContextsService.fetchPrivateData(this.contactId, EntityType.CONTACT);
            }
        } else {
            this.targetingRhpContextsService.setOutlet(this.outletId);
        }
    }
}
